'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import Image from 'next/image'
import { useRouter } from '@/i18n/navigation'
import { fetchSidebarData, fetchSidebarChart, type SidebarData, type SidebarQuote, type SidebarMover } from '@/services/sidebarApi'
import { usePolledData } from '@/hooks/usePolledData'
import { useLocale } from 'next-intl'
import { t } from '@/i18n/translations'
import { brokers , formatCommission } from '@/data/indexData'
import { trackAffiliateClick } from '@/utils/analytics'
import { useSignalR } from '@/hooks/useSignalR'
import type { PriceUpdate } from '@/services/signalrService'

/* ───── Tipler ───── */
interface QuoteItem {
  id: string
  name: string
  value: number
  change: number
  changePercent: number
}

interface MoverItem {
  id: string
  name: string
  symbol: string
  value: number
  changePercent: number
  volume: string
}

/* ───── SVG Icon Bileşenleri ───── */
function IconPiyasalar() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  )
}
function IconAraciKurum() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <polyline points="17 11 19 13 23 9" />
    </svg>
  )
}

/* ───── Sidebar Tab Tanımları ───── */
type SidebarTabId = 'piyasalar' | 'araci'

const sidebarTabDefs: { id: SidebarTabId; i18n: string; icon: () => React.JSX.Element }[] = [
  { id: 'piyasalar', i18n: 'sidebar.markets', icon: IconPiyasalar },
  { id: 'araci', i18n: 'sidebar.brokers', icon: IconAraciKurum },
]

/* ───── Yapılandırma ───── */

type QuoteTabKey = 'indices' | 'forex' | 'commodities' | 'stocks'
const quoteTabConfig: { key: QuoteTabKey; i18n: string }[] = [
  { key: 'indices', i18n: 'sidebar.indices' },
  { key: 'forex', i18n: 'sidebar.forex' },
  { key: 'commodities', i18n: 'sidebar.commodities' },
  { key: 'stocks', i18n: 'sidebar.stocks' },
]

/* ───── Yardımcı Fonksiyonlar ───── */
function fmtVal(val: number): string {
  if (val === 0) return '—'
  if (Math.abs(val) >= 1000) {
    const f = val.toFixed(2).split('.')
    return f[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + f[1]
  }
  return val.toFixed(val < 10 ? 4 : val < 100 ? 3 : 2).replace('.', ',')
}

function fmtChg(val: number): string {
  if (val === 0) return '0,00'
  const p = val > 0 ? '+' : ''
  if (Math.abs(val) >= 1000) {
    const f = val.toFixed(2).split('.')
    return p + f[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + f[1]
  }
  return p + val.toFixed(Math.abs(val) < 1 ? 3 : 2).replace('.', ',')
}

function fmtPct(val: number): string {
  const p = val > 0 ? '+' : ''
  return p + val.toFixed(2).replace('.', ',') + '%'
}

function clr(val: number): string {
  return val > 0 ? 'var(--chart-up)' : val < 0 ? 'var(--chart-down)' : 'var(--text-muted)'
}

/* ───── Backend → Frontend dönüştürücüler ───── */
function toQuoteItems(quotes: SidebarQuote[]): QuoteItem[] {
  return quotes.map(q => ({
    id: q.symbol,
    name: q.name,
    value: q.value,
    change: q.change,
    changePercent: q.changePercent,
  }))
}

function toMoverItems(movers: SidebarMover[]): MoverItem[] {
  return movers.map(m => ({
    id: m.symbol,
    symbol: m.symbol,
    name: m.name,
    value: m.value,
    changePercent: m.changePercent,
    volume: m.volume,
  }))
}

/* ───── Mini Chart ───── */
function MiniChart({ color, data }: { color: string; data: number[] }) {
  const w = 300, h = 130
  const min = Math.min(...data), max = Math.max(...data), range = max - min || 1
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w
    const y = h - 12 - ((v - min) / range) * (h - 24)
    return `${x},${y}`
  }).join(' ')
  const area = `M0,${h} ` + data.map((v, i) => {
    const x = (i / (data.length - 1)) * w
    const y = h - 12 - ((v - min) / range) * (h - 24)
    return `L${x},${y}`
  }).join(' ') + ` L${w},${h} Z`
  const gid = `sg-${color.replace(/[^a-z0-9]/gi, '')}`
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: 'block' }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" style={{ stopColor: color, stopOpacity: 0.12 }} />
          <stop offset="100%" style={{ stopColor: color, stopOpacity: 0.01 }} />
        </linearGradient>
      </defs>
      <path d={area} style={{ fill: `url(#${gid})` }} />
      <polyline points={pts} fill="none" style={{ stroke: color }} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}

/* ───── Veri Tablo Satırı ───── */
function DataRow({ item, isLast, onClick }: { item: MoverItem; isLast: boolean; onClick?: () => void }) {
  return (
    <div
      className="flex items-center px-3 py-[7px] cursor-pointer transition-colors duration-150 hover:bg-[var(--bg-body)]"
      style={{ borderBottom: isLast ? 'none' : '1px solid var(--border-color)' }}
      onClick={onClick}
    >
      <div className="flex-1 min-w-0">
        <span className="text-[11.5px] font-semibold truncate block" style={{ color: 'var(--text-main)' }}>{item.name}</span>
      </div>
      <div className="w-[62px] text-right text-[11.5px] tabular-nums font-medium" style={{ color: 'var(--text-main)' }}>
        {fmtVal(item.value)}
      </div>
      <div className="w-[56px] text-right text-[11.5px] tabular-nums font-semibold" style={{ color: clr(item.changePercent) }}>
        {fmtPct(item.changePercent)}
      </div>
      <div className="w-[62px] text-right text-[10px] tabular-nums" style={{ color: 'var(--text-muted)' }}>
        {item.volume}
      </div>
      <div className="w-[18px] flex items-center justify-center ml-0.5">
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="6" stroke="var(--text-muted)" strokeWidth="1.2" opacity="0.4" />
          <path d="M7 4v3l2 1" stroke="var(--text-muted)" strokeWidth="1.2" strokeLinecap="round" opacity="0.5" />
        </svg>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════
   ANA BILESEN
   ═══════════════════════════════════════════ */
export default function MarketSidebar({
  initialData,
  initialChart,
}: {
  initialData?: SidebarData | null
  /**
   * E2 (Temmuz 2026 denetimi): grafik alanı, kenar çubuğunun SSR'lanmayan tek
   * parçasıydı — ham HTML'de hep sabit "Grafik yükleniyor…" metni geliyordu.
   * Açılıştaki grafik (ilk endeks, varsayılan "1A" periyodu) artık sunucuda çekilip
   * buraya veriliyor.
   */
  initialChart?: number[] | null
}) {
  const lang = useLocale()
  const router = useRouter()

  /* ───── Tab State ───── */
  const [sidebarTab, setSidebarTab] = useState<SidebarTabId>('piyasalar')
  const [quoteTab, setQuoteTab] = useState<QuoteTabKey>('indices')
  const [selIdx, setSelIdx] = useState(0)
  const [moverTab, setMoverTab] = useState<'active' | 'gainers' | 'losers'>('active')
  const [period, setPeriod] = useState('1A')

  /* ───── Veri State ───── */
  // SSR seed: useState initializer SUNUCUDA çalışır → içerik ham HTML'de gelir.
  const [endeksler, setEndeksler] = useState<QuoteItem[]>(() => initialData ? toQuoteItems(initialData.indices) : [])
  const [dovizler, setDovizler] = useState<QuoteItem[]>(() => initialData ? toQuoteItems(initialData.forex) : [])
  const [emtiaQuotes, setEmtiaQuotes] = useState<QuoteItem[]>(() => initialData ? toQuoteItems(initialData.commodities) : [])
  const [hisseler, setHisseler] = useState<QuoteItem[]>(() => initialData ? toQuoteItems(initialData.stocks) : [])

  const [chartData, setChartData] = useState<number[]>(() => initialChart ?? [])
  const [chartLoading, setChartLoading] = useState(false)
  // Tazeleme sırasında ekrandaki grafiği "yükleniyor" ile değiştirmemek için
  // mevcut veriyi effect içinden bayat kapanış olmadan okumak gerekiyor.
  const chartDataRef = useRef(chartData)
  chartDataRef.current = chartData

  const [moversActiveData, setMoversActiveData] = useState<MoverItem[]>(() => initialData ? toMoverItems(initialData.movers.active) : [])
  const [moversGainersData, setMoversGainersData] = useState<MoverItem[]>(() => initialData ? toMoverItems(initialData.movers.gainers) : [])
  const [moversLosersData, setMoversLosersData] = useState<MoverItem[]>(() => initialData ? toMoverItems(initialData.movers.losers) : [])
  const [trendingData, setTrendingData] = useState<MoverItem[]>(() => initialData ? toMoverItems(initialData.trending) : [])

  /* ───── Tek API'dan tüm veri — SSR'lı (initialData) + DRY polling hook ───── */
  const { data: sidebarData } = usePolledData(
    () => fetchSidebarData(lang),
    initialData ?? undefined,
    30_000,
  )
  useEffect(() => {
    if (!sidebarData) return
    setEndeksler(toQuoteItems(sidebarData.indices))
    setDovizler(toQuoteItems(sidebarData.forex))
    setEmtiaQuotes(toQuoteItems(sidebarData.commodities))
    setHisseler(toQuoteItems(sidebarData.stocks))
    setMoversActiveData(toMoverItems(sidebarData.movers.active))
    setMoversGainersData(toMoverItems(sidebarData.movers.gainers))
    setMoversLosersData(toMoverItems(sidebarData.movers.losers))
    setTrendingData(toMoverItems(sidebarData.trending))
  }, [sidebarData])

  /* ───── SignalR Canlı Fiyat ───── */
  const handleSidebarPriceUpdate = useCallback((update: PriceUpdate) => {
    // Quote listeleri (Döviz, Emtia, Hisseler)
    const updateQuoteList = (setter: React.Dispatch<React.SetStateAction<QuoteItem[]>>) => {
      setter(prev => {
        const idx = prev.findIndex(q => q.id === update.symbol)
        if (idx === -1) return prev
        if (prev[idx].value === update.price) return prev
        const updated = [...prev]
        updated[idx] = { ...updated[idx], value: update.price }
        return updated
      })
    }
    updateQuoteList(setDovizler)
    updateQuoteList(setEmtiaQuotes)
    updateQuoteList(setHisseler)

    // Movers & Trending listeleri
    const updateMoverList = (setter: React.Dispatch<React.SetStateAction<MoverItem[]>>) => {
      setter(prev => {
        const idx = prev.findIndex(m => m.symbol === update.symbol)
        if (idx === -1) return prev
        if (prev[idx].value === update.price) return prev
        const updated = [...prev]
        updated[idx] = { ...updated[idx], value: update.price }
        return updated
      })
    }
    updateMoverList(setMoversActiveData)
    updateMoverList(setMoversGainersData)
    updateMoverList(setMoversLosersData)
    updateMoverList(setTrendingData)
  }, [])

  useSignalR({
    onPriceUpdate: handleSidebarPriceUpdate,
  })

  /* ───── Grafik Verisi ───── */
  useEffect(() => {
    const hasChart = quoteTab === 'indices'
    if (!hasChart) {
      setChartData([])
      return
    }

    let cancelled = false
    // Elde gösterilecek bir grafik varsa (SSR'dan gelen dahil) yükleme durumuna
    // düşmeyiz; aksi hâlde SSR ile kazanılan içerik anında kaybolurdu.
    if (chartDataRef.current.length === 0) setChartLoading(true)

    const curQuotes = endeksler

    const sel = curQuotes[selIdx]
    if (!sel) {
      setChartLoading(false)
      return
    }

    fetchSidebarChart(sel.id, period).then(points => {
      if (!cancelled) {
        setChartData(points)
        setChartLoading(false)
      }
    })

    return () => { cancelled = true }
  }, [quoteTab, selIdx, period, endeksler])

  /* ───── Hesaplanan Değerler ───── */
  const curQuotes =
    quoteTab === 'indices' ? endeksler :
    quoteTab === 'forex' ? dovizler :
    quoteTab === 'commodities' ? emtiaQuotes :
    hisseler

  const sel = curQuotes[selIdx] || curQuotes[0]
  const cColor = sel?.changePercent >= 0 ? '#84b2da' : '#e53935'
  const movers = moverTab === 'active' ? moversActiveData : moverTab === 'gainers' ? moversGainersData : moversLosersData
  const hasChart = quoteTab === 'indices'
  const periods = ['1A', '1Y', '5Y']

  /* ───── Tıklama Yönlendirme ───── */
  const handleQuoteClick = (idx: number) => {
    if (hasChart) {
      setSelIdx(idx)
    } else {
      const item = curQuotes[idx]
      if (item) handleQuoteNavigate(item)
    }
  }

  const handleQuoteNavigate = (item: QuoteItem) => {
    switch (quoteTab) {
      case 'indices':
      case 'commodities':
        router.push({ pathname: '/market/[id]', params: { id: item.id.toLowerCase() } })
        break
      case 'forex':
        router.push({ pathname: '/forex/[pair]', params: { pair: item.id } })
        break
      case 'stocks':
        router.push({ pathname: '/stock/[symbol]', params: { symbol: item.id } })
        break
    }
  }

  /* ───── İçerik Panelleri ───── */
  function renderPiyasalar() {
    return (
      <div className="flex flex-col">
        {/* Quotes Box */}
        <div>
          {/* Quote Tab Bar */}
          <div className="flex items-center px-3 pt-1.5 pb-1.5 gap-0 flex-wrap">
            {quoteTabConfig.map((qt, i) => (
              <div key={qt.key} className="flex items-center">
                {i > 0 && <div className="mx-2 h-3.5 w-px" style={{ backgroundColor: 'var(--border-color)' }} />}
                <button
                  onClick={() => { setQuoteTab(qt.key); setSelIdx(0) }}
                  className="text-[11.5px] font-semibold transition-colors py-2"
                  style={{ color: quoteTab === qt.key ? 'var(--accent-color)' : 'var(--text-muted)' }}
                >{t(qt.i18n, lang)}</button>
              </div>
            ))}
            <button aria-label={t('aria.moreOptions', lang)} className="ml-auto px-2 py-2" style={{ color: 'var(--text-muted)' }}>
              <svg width="4" height="12" viewBox="0 0 4 12" fill="currentColor"><circle cx="2" cy="2" r="1.3" /><circle cx="2" cy="6" r="1.3" /><circle cx="2" cy="10" r="1.3" /></svg>
            </button>
          </div>

          {/* Period selector + Chart (sadece Endeksler ve Hisseler) */}
          {hasChart && (
            <>
              <div className="flex items-center px-3 pt-1 pb-0.5">
                {periods.map(p => (
                  <button key={p} onClick={() => setPeriod(p)}
                    className="px-2 py-1.5 text-[10px] transition-colors"
                    style={{ fontWeight: period === p ? 700 : 400, color: period === p ? 'var(--text-main)' : 'var(--text-muted)', textDecoration: period === p ? 'underline' : 'none' }}
                  >{p}</button>
                ))}
              </div>

              <div className="px-2 mt-1">
                {chartLoading ? (
                  <div className="animate-pulse rounded-lg" style={{ height: 130, backgroundColor: 'var(--border-color)', opacity: 0.3 }} />
                ) : chartData.length > 0 ? (
                  <MiniChart color={cColor} data={chartData} />
                ) : (
                  <div className="flex items-center justify-center text-[11px]" style={{ height: 130, color: 'var(--text-muted)' }}>
                    {t('status.loadingChart', lang)}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Quote List */}
          <div className="pt-0.5 pb-1">
            {curQuotes.map((q, i) => {
              const isActive = selIdx === i
              return (
                <div key={q.id}
                  onClick={() => handleQuoteClick(i)}
                  onDoubleClick={() => handleQuoteNavigate(q)}
                  className={`flex items-center px-3 py-[6px] cursor-pointer transition-colors duration-150 ${isActive ? '' : 'hover:bg-[var(--bg-body)]'}`}
                  style={{
                    borderBottom: i < curQuotes.length - 1 ? '1px solid var(--border-color)' : 'none',
                    backgroundColor: isActive ? 'color-mix(in srgb, var(--accent-color) 6%, transparent)' : 'transparent',
                    borderRadius: isActive ? 3 : 0,
                  }}
                >
                  <div className="w-[88px] text-[11.5px] font-bold truncate" style={{ color: 'var(--text-main)' }}>{q.name}</div>
                  <div className="flex-1 text-right text-[11.5px] tabular-nums font-medium" style={{ color: 'var(--text-main)' }}>{fmtVal(q.value)}</div>
                  <div className="w-[60px] text-right text-[11.5px] tabular-nums font-medium" style={{ color: clr(q.changePercent) }}>{fmtChg(q.change)}</div>
                  <div className="w-[52px] text-right text-[11.5px] tabular-nums font-medium" style={{ color: clr(q.changePercent) }}>{fmtPct(q.changePercent)}</div>
                  <div
                    className="w-[16px] flex justify-center ml-1"
                    onClick={e => { e.stopPropagation(); handleQuoteNavigate(q) }}
                    title="Detay sayfasına git"
                  >
                    <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                      <polyline points="5 3 10 7 5 11" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.5" />
                    </svg>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Piyasaya Yon Verenler */}
        <div className="border-t mt-4 md:mt-0 pt-1 md:pt-0" style={{ borderColor: 'var(--border-color)' }}>
          <div className="px-3 pt-3 pb-2 flex items-center justify-between">
            <h3 className="text-[14px] font-bold m-0 flex items-center gap-1.5" style={{ color: 'var(--text-main)' }}>
              {t('sidebar.movers', lang)}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>
            </h3>
          </div>
          {/* Mover Tabs */}
          <div className="flex items-center px-3 border-b" style={{ borderColor: 'var(--border-color)' }}>
            {(['active', 'gainers', 'losers'] as const).map((tab, i) => {
              const lblKey = { active: 'sidebar.active', gainers: 'sidebar.gainers', losers: 'sidebar.losers' }
              return (
                <div key={tab} className="flex items-center">
                  {i > 0 && <div className="mx-2.5 h-3.5 w-px" style={{ backgroundColor: 'var(--border-color)' }} />}
                  <button onClick={() => setMoverTab(tab)}
                    className="-mb-px py-2 text-[11.5px] font-semibold border-b-2 transition-colors"
                    style={{ borderColor: moverTab === tab ? 'var(--accent-color)' : 'transparent', color: moverTab === tab ? 'var(--accent-color)' : 'var(--text-muted)' }}
                  >{t(lblKey[tab], lang)}</button>
                </div>
              )
            })}
          </div>
          {/* Table Header */}
          <div className="flex items-center px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)' }}>
            <div className="flex-1">{t('th.name', lang)}</div>
            <div className="w-[62px] text-right">{t('th.lastPrice', lang)}</div>
            <div className="w-[56px] text-right">{t('th.changePercent', lang)}</div>
            <div className="w-[62px] text-right">{t('th.volume', lang)}</div>
            <div className="w-[18px]" />
          </div>
          {movers.length > 0 ? (
            movers.map((m, i) => (
              <DataRow key={m.id} item={m} isLast={i === movers.length - 1} onClick={() => router.push({ pathname: '/stock/[symbol]', params: { symbol: m.symbol } })} />
            ))
          ) : (
            <div className="px-3 py-6 text-center text-[11px]" style={{ color: 'var(--text-muted)' }}>
              {t('status.loadingData', lang)}
            </div>
          )}
        </div>

        {/* Öncü Hisseler */}
        <div className="border-t mt-4 md:mt-0 pt-1 md:pt-0" style={{ borderColor: 'var(--border-color)' }}>
          <div className="px-3 pt-3 pb-2 flex items-center justify-between">
            <h3 className="text-[14px] font-bold m-0 flex items-center gap-1.5" style={{ color: 'var(--text-main)' }}>
              {t('sidebar.leading', lang)}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>
            </h3>
          </div>
          <div className="flex items-center px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider" style={{ color: 'var(--text-muted)', borderBottom: '1px solid var(--border-color)' }}>
            <div className="flex-1">{t('th.name', lang)}</div>
            <div className="w-[62px] text-right">{t('th.lastPrice', lang)}</div>
            <div className="w-[56px] text-right">{t('th.changePercent', lang)}</div>
            <div className="w-[62px] text-right">{t('th.volume', lang)}</div>
            <div className="w-[18px]" />
          </div>
          {trendingData.length > 0 ? (
            trendingData.map((m, i) => (
              <DataRow key={m.id} item={m} isLast={i === trendingData.length - 1} onClick={() => router.push({ pathname: '/stock/[symbol]', params: { symbol: m.symbol } })} />
            ))
          ) : (
            <div className="px-3 py-6 text-center text-[11px]" style={{ color: 'var(--text-muted)' }}>
              {t('status.loadingData', lang)}
            </div>
          )}
          <div className="px-3 py-2.5 text-center text-[11px] cursor-pointer transition-colors border-t font-medium hover:bg-[var(--bg-body)] hover:text-[var(--accent-color)]"
            style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color)' }}
            onClick={() => router.push({ pathname: '/stocks' })}
          >{t('sidebar.showMore', lang)}</div>
        </div>

      </div>
    )
  }

  const brokerWebsites: Record<string, string> = {
    midas: 'https://www.getmidas.com',
    garanti: 'https://www.garantibbvayatirim.com.tr',
    istefon: 'https://www.isyatirim.com.tr',
    akbank: 'https://www.akyatirim.com.tr',
  }

  function renderAraciKurumlar() {
    return (
      <div className="flex flex-col">
        {/* Başlık */}
        <div className="px-3 pt-3 pb-2">
          <h3 className="text-[14px] font-bold m-0" style={{ color: 'var(--text-main)' }}>
            {t('sidebar.platforms', lang)}
          </h3>
          <p className="text-[11px] mt-1 m-0" style={{ color: 'var(--text-muted)' }}>
            {t('sidebar.platformsDesc', lang)}
          </p>
        </div>

        {/* Broker Kartları */}
        <div className="px-3 pb-3">
          {brokers.map((b, i) => (
            <div
              key={b.id}
              className="flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors duration-150 hover:bg-[var(--bg-body)]"
              style={{ borderBottom: i < brokers.length - 1 ? '1px solid var(--border-color)' : 'none' }}
              onClick={() => {
                const url = brokerWebsites[b.id]
                if (url) {
                  trackAffiliateClick(b.id, b.name, 'sidebar', url)
                  window.open(url, '_blank')
                }
              }}
            >
              <div className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden p-1" style={{ backgroundColor: b.logoBg || '#ffffff' }}>
                <Image
                  src={b.logo}
                  alt={b.name}
                  width={32}
                  height={32}
                  className="w-full h-full object-contain"
                  unoptimized
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-bold truncate" style={{ color: 'var(--text-main)' }}>
                  {b.name}
                </div>
              </div>
              <span
                className="text-[11px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                style={{
                  backgroundColor: b.commissionPct === null ? 'var(--chart-up)' : 'var(--secondary-color)',
                  color: '#fff',
                }}
              >
                {formatCommission(b.commissionPct, lang)}
              </span>
            </div>
          ))}
        </div>

        {/* Tüm Platformları Gör */}
        <div
          className="px-3 py-2.5 text-center text-[11px] cursor-pointer transition-colors border-t font-medium hover:bg-[var(--bg-body)] hover:text-[var(--accent-color)]"
          style={{ color: 'var(--text-muted)', borderColor: 'var(--border-color)' }}
          onClick={() => router.push('/brokers')}
        >
          {t('sidebar.viewAll', lang)}
        </div>

        {/* Bilgilendirme */}
        <div className="px-3 py-3 border-t" style={{ borderColor: 'var(--border-color)' }}>
          <p className="text-[9px] leading-relaxed m-0" style={{ color: 'var(--text-muted)' }}>
            {t('sidebar.disclaimer', lang)}
          </p>
        </div>
      </div>
    )
  }

  function renderContent() {
    switch (sidebarTab) {
      case 'piyasalar': return renderPiyasalar()
      case 'araci': return renderAraciKurumlar()
    }
  }

  /* ───── RENDER ───── */
  return (
    <div
      className="border overflow-hidden"
      style={{ borderColor: 'var(--border-color)', borderRadius: 8, backgroundColor: 'var(--bg-card)' }}
    >
      {/* Mobil: Yatay Tab Bar (üstte) */}
      <div
        className="flex md:hidden items-center border-b"
        style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-body)' }}
      >
        {sidebarTabDefs.map(tab => {
          const Icon = tab.icon
          const isActive = sidebarTab === tab.id
          return (
            <button
              key={tab.id}
              onClick={() => setSidebarTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 transition-colors duration-150 border-b-2${tab.id === 'araci' ? ' tab-araci-glow' : ''}`}
              style={{
                color: isActive ? 'var(--accent-color)' : 'var(--text-muted)',
                borderColor: isActive ? 'var(--accent-color)' : 'transparent',
                backgroundColor: isActive ? 'color-mix(in srgb, var(--accent-color) 8%, transparent)' : 'transparent',
              }}
            >
              <Icon />
              <span className="text-[11px] font-semibold">
                {t(tab.i18n, lang)}
              </span>
            </button>
          )
        })}
      </div>

      <div className="flex">
        {/* İçerik Paneli */}
        <div className="flex-1 min-w-0 md:border-r" style={{ borderColor: 'var(--border-color)' }}>
          {renderContent()}
        </div>

        {/* Desktop: Dikey Tab Bar (sağda) */}
        <div
          className="hidden md:flex flex-shrink-0 w-[64px] flex-col items-center gap-0"
          style={{ backgroundColor: 'var(--bg-body)' }}
        >
          {sidebarTabDefs.map(tab => {
            const Icon = tab.icon
            const isActive = sidebarTab === tab.id
            return (
              <button
                key={tab.id}
                onClick={() => setSidebarTab(tab.id)}
                className={`flex flex-col items-center justify-center w-full px-1 py-3 transition-colors duration-150 ${isActive ? '' : 'hover:bg-[var(--bg-card)] hover:text-[var(--text-main)]'}${tab.id === 'araci' ? ' tab-araci-glow' : ''}`}
                style={{
                  color: isActive ? 'var(--accent-color)' : 'var(--text-muted)',
                  backgroundColor: isActive ? 'color-mix(in srgb, var(--accent-color) 8%, transparent)' : 'transparent',
                }}
                title={t(tab.i18n, lang)}
              >
                <Icon />
                <span className="text-[9px] leading-[11px] mt-1 text-center font-medium" style={{ maxWidth: 58, wordBreak: 'break-word' }}>
                  {t(tab.i18n, lang)}
                </span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
